/**
 * Chairside Mode — a distraction-free, touch-first clinical UI designed for
 * an iPad/tablet placed next to the dental chair.
 *
 *  Goals
 *  -----
 *  • Zero navigation chrome. Just the chart, the patient, and the clinical
 *    tools (conditions, notes, voice dictation, photos).
 *  • Big, gloved-hand-friendly touch targets (≥ 44 px).
 *  • Landscape *and* portrait iPad orientations look great.
 *  • Swipe left/right on the chart to hop between adjacent teeth.
 *  • Sticky bottom action bar for the most-common actions.
 *
 *  Route: /app/chairside/:id (registered in App.js, outside DashboardLayout).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, X, Maximize2, Minimize2, Loader2,
  Save, Camera, Mic, MicOff, Image as ImageIcon, StickyNote,
  Stethoscope, Trash2, Plus, ChevronLeft, ChevronRight, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import ToothSVG, { CONDITION_META, SURFACE_LABELS, toothInfo, ConditionIcon }
  from "@/components/app/ToothSVG";
import TreatmentPalette from "@/components/app/TreatmentPalette";

// FDI layout — same ordering as the dashboard DentalChartTab.
const PERMANENT = {
  "upper-right": ["18","17","16","15","14","13","12","11"],
  "upper-left":  ["21","22","23","24","25","26","27","28"],
  "lower-left":  ["31","32","33","34","35","36","37","38"],
  "lower-right": ["48","47","46","45","44","43","42","41"],
};
const PRIMARY = {
  "upper-right": ["55","54","53","52","51"],
  "upper-left":  ["61","62","63","64","65"],
  "lower-left":  ["71","72","73","74","75"],
  "lower-right": ["85","84","83","82","81"],
};

// Quick-tap condition palette (most-used at chairside).
const QUICK_CONDITIONS = [
  "caries", "filling", "amalgam", "crown", "rct", "extracted",
  "missing", "sealant", "fracture", "mobility", "veneer", "implant",
  "extraction_indicated", "temp_restoration", "calculus", "wear",
];

const TABS = [
  { key: "conditions", label: "Conditions", icon: Stethoscope },
  { key: "notes",      label: "Notes",      icon: StickyNote },
  { key: "voice",      label: "Voice",      icon: Mic },
  { key: "photos",     label: "Photos",     icon: Camera },
];

export default function ChairsideMode() {
  const { id } = useParams();
  const nav = useNavigate();

  const [patient, setPatient] = useState(null);
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [selectedSurfaces, setSelectedSurfaces] = useState([]);
  const [tab, setTab] = useState("conditions");
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [toothFiles, setToothFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const [orientation, setOrientation] = useState("landscape");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const rootRef = useRef(null);

  // Ctrl/⌘+K opens the treatment palette anywhere in Chairside Mode.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen(true);
      } else if (e.key === "Escape" && paletteOpen) {
        setPaletteOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paletteOpen]);

  // -------- initial load --------
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, c] = await Promise.all([
          api.get(`/patients/${id}`),
          api.get(`/patients/${id}/dental-chart`),
        ]);
        setPatient(p.data);
        setChart(c.data);
      } catch {
        toast.error("Could not load patient");
      } finally { setLoading(false); }
    })();
  }, [id]);

  // -------- orientation watcher (landscape vs portrait) --------
  useEffect(() => {
    const onResize = () => {
      setOrientation(window.innerWidth >= window.innerHeight ? "landscape" : "portrait");
    };
    onResize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // -------- fullscreen --------
  const toggleFullscreen = () => {
    const el = rootRef.current || document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setIsFs(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFs(false)).catch(() => {});
    }
  };
  useEffect(() => {
    const on = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);

  // -------- selection effect: preload notes and tooth files --------
  useEffect(() => {
    if (!selectedTooth) { setToothFiles([]); return; }
    setNotesDraft(chart?.teeth?.[selectedTooth]?.notes || "");
    setSelectedSurfaces([]);
    api.get(`/patients/${id}/dental-chart/teeth/${selectedTooth}/files`)
      .then(({ data }) => setToothFiles(data || []))
      .catch(() => setToothFiles([]));
  }, [selectedTooth, id, chart]);

  const dentition = chart?.dentition || "permanent";
  const layout = useMemo(() => {
    if (dentition === "primary") return PRIMARY;
    if (dentition === "mixed")   return null;
    return PERMANENT;
  }, [dentition]);

  const allTeethInOrder = useMemo(() => {
    const src = dentition === "primary" ? PRIMARY : PERMANENT;
    return [
      ...src["upper-right"], ...src["upper-left"],
      ...src["lower-right"], ...src["lower-left"],
    ];
  }, [dentition]);

  const setDentition = async (v) => {
    if (!chart) return;
    try {
      await api.patch(`/patients/${id}/dental-chart`, { dentition: v });
      setChart({ ...chart, dentition: v });
      setSelectedTooth(null);
    } catch { toast.error("Could not switch dentition"); }
  };

  // -------- actions --------
  const applyCondition = async (code) => {
    if (!selectedTooth) return toast.error("Tap a tooth first");
    try {
      const { data } = await api.post(
        `/patients/${id}/dental-chart/teeth/${selectedTooth}/conditions`,
        { code, surfaces: selectedSurfaces });
      setChart((c) => ({ ...c, teeth: { ...c.teeth, [selectedTooth]: data.tooth } }));
      toast.success(`${CONDITION_META[code].label} · ${selectedTooth}`);
    } catch (e) { toast.error(apiError(e, "Failed")); }
  };

  const removeCondition = async (cid) => {
    if (!selectedTooth) return;
    try {
      const { data } = await api.delete(
        `/patients/${id}/dental-chart/teeth/${selectedTooth}/conditions/${cid}`);
      setChart((c) => ({ ...c, teeth: { ...c.teeth, [selectedTooth]: data.tooth } }));
    } catch (e) { toast.error(apiError(e, "Failed")); }
  };

  const planTreatment = async (proc) => {
    if (!selectedTooth) return toast.error("Tap a tooth first");
    try {
      await api.post(
        `/patients/${id}/dental-chart/teeth/${selectedTooth}/plan-treatment`,
        {
          procedure: proc.name,
          cost: proc.cost,
          surfaces: selectedSurfaces,
          duration_min: proc.duration_min || null,
        });
    } catch (e) {
      // Palette shows aggregate toast — surface individual failure detail if any.
      toast.error(apiError(e, `Failed: ${proc.name}`));
      throw e;
    }
  };

  const saveNotes = async () => {
    if (!selectedTooth) return;
    setSavingNotes(true);
    try {
      const { data } = await api.patch(
        `/patients/${id}/dental-chart/teeth/${selectedTooth}`, { notes: notesDraft });
      setChart((c) => ({ ...c, teeth: { ...c.teeth, [selectedTooth]: data.tooth } }));
      toast.success("Note saved");
    } catch { toast.error("Failed to save"); }
    finally { setSavingNotes(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const kind = file.type.startsWith("image/") ? "photo" : "document";
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data: rec } = await api.post(`/patients/${id}/files?kind=${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Auto-link to the currently selected tooth for chairside speed
      if (selectedTooth) {
        await api.post(`/patient-files/${rec.id}/link-tooth`, { tooth: selectedTooth });
      }
      toast.success(selectedTooth ? `Photo attached to ${selectedTooth}` : "Photo uploaded");
      if (selectedTooth) {
        const { data } = await api.get(`/patients/${id}/dental-chart/teeth/${selectedTooth}/files`);
        setToothFiles(data || []);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally { setUploading(false); }
  };

  // -------- keyboard shortcuts & prev/next tooth --------
  const gotoAdjacent = useCallback((dir) => {
    if (!selectedTooth) {
      setSelectedTooth(allTeethInOrder[0]);
      return;
    }
    const i = allTeethInOrder.indexOf(selectedTooth);
    if (i === -1) return;
    const next = dir === "next"
      ? allTeethInOrder[(i + 1) % allTeethInOrder.length]
      : allTeethInOrder[(i - 1 + allTeethInOrder.length) % allTeethInOrder.length];
    setSelectedTooth(next);
  }, [selectedTooth, allTeethInOrder]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.matches?.("input,textarea")) return;
      if (e.key === "ArrowRight") gotoAdjacent("next");
      if (e.key === "ArrowLeft")  gotoAdjacent("prev");
      if (e.key === "Escape")     nav(`/app/patients/${id}`);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [gotoAdjacent, id, nav]);

  // -------- swipe support on the chart --------
  const swipeStartRef = useRef(null);
  const onTouchStart = (e) => {
    if (e.touches?.length !== 1) return;
    swipeStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    const s = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!s) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) < 60) return;
    if (Math.abs(dx) < Math.abs(dy) * 1.4) return; // must be horizontal
    if (Date.now() - s.t > 600) return;
    gotoAdjacent(dx < 0 ? "next" : "prev");
  };

  // -------- guards / loading state --------
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1D18] text-linen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!patient || !chart) {
    return (
      <div className="min-h-screen bg-[#0F1D18] text-linen grid place-items-center">
        <div className="text-center space-y-3">
          <div className="text-sm opacity-70">Could not open chairside mode.</div>
          <button onClick={() => nav("/app/patients")} className="text-xs underline">Go back</button>
        </div>
      </div>
    );
  }

  const currentTooth = selectedTooth ? chart.teeth[selectedTooth] : null;
  const info = selectedTooth ? toothInfo(selectedTooth) : null;

  return (
    <div
      ref={rootRef}
      data-testid="chairside-mode"
      data-orientation={orientation}
      className="fixed inset-0 z-40 bg-[#0F1D18] text-linen flex flex-col"
      style={{ WebkitTapHighlightColor: "transparent", touchAction: "manipulation" }}
    >
      {/* Header */}
      <header className="h-14 shrink-0 bg-[#0B1712] border-b border-white/5 flex items-center justify-between px-3 sm:px-4 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            data-testid="chairside-exit"
            onClick={() => nav(`/app/patients/${id}`)}
            className="w-11 h-11 rounded-full grid place-items-center hover:bg-white/10 active:scale-95 transition">
            <X className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Chairside mode
            </div>
            <div className="font-display text-lg truncate leading-tight" data-testid="chairside-patient-name">
              {patient.name}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <div className="hidden sm:flex items-center gap-0.5 bg-white/5 rounded-full p-0.5 text-[11px]" data-testid="chairside-dentition">
            {["permanent", "mixed", "primary"].map((v) => (
              <button
                key={v} data-testid={`chairside-dentition-${v}`}
                onClick={() => setDentition(v)}
                className={`px-3 py-1.5 rounded-full min-h-[36px] ${dentition === v ? "bg-linen text-ink" : "text-white/70"}`}>
                {v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <button
            data-testid="chairside-fullscreen"
            onClick={toggleFullscreen}
            className="w-11 h-11 rounded-full grid place-items-center hover:bg-white/10 active:scale-95 transition"
            title={isFs ? "Exit fullscreen" : "Enter fullscreen"}>
            {isFs ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Body */}
      <div
        className={`flex-1 min-h-0 flex ${orientation === "landscape" ? "flex-row" : "flex-col"} overflow-hidden`}
      >
        {/* CHART pane */}
        <section
          data-testid="chairside-chart"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          className={`${orientation === "landscape" ? "flex-[1.15] border-r" : "flex-[0.6] border-b"} border-white/5 relative overflow-auto p-4 sm:p-6 bg-gradient-to-br from-[#0F1D18] to-[#0B1712]`}
        >
          {/* Mobile dentition toggle */}
          <div className="sm:hidden mb-3 flex justify-center">
            <div className="flex items-center gap-0.5 bg-white/5 rounded-full p-0.5 text-[11px]">
              {["permanent", "mixed", "primary"].map((v) => (
                <button
                  key={v} onClick={() => setDentition(v)}
                  className={`px-2.5 py-1 rounded-full ${dentition === v ? "bg-linen text-ink" : "text-white/70"}`}>
                  {v[0].toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="chairside-chart-inner mx-auto max-w-full">
            {dentition === "mixed" ? (
              <div className="space-y-6">
                <ChairArch teeth={PERMANENT} chart={chart}
                  selectedTooth={selectedTooth} onTooth={setSelectedTooth}
                  selectedSurfaces={selectedSurfaces} onSurface={(t, s) => {
                    if (selectedTooth !== t) setSelectedTooth(t);
                    setSelectedSurfaces((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);
                  }} title="Permanent" />
                <ChairArch teeth={PRIMARY} chart={chart}
                  selectedTooth={selectedTooth} onTooth={setSelectedTooth}
                  selectedSurfaces={selectedSurfaces} onSurface={(t, s) => {
                    if (selectedTooth !== t) setSelectedTooth(t);
                    setSelectedSurfaces((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);
                  }} title="Primary" />
              </div>
            ) : (
              <ChairArch teeth={layout} chart={chart}
                selectedTooth={selectedTooth} onTooth={setSelectedTooth}
                selectedSurfaces={selectedSurfaces} onSurface={(t, s) => {
                  if (selectedTooth !== t) setSelectedTooth(t);
                  setSelectedSurfaces((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);
                }} />
            )}
          </div>

          {!selectedTooth && (
            <div className="mt-6 text-center text-white/50 text-sm">
              Tap any tooth to open its clinical panel · swipe to move between adjacent teeth.
            </div>
          )}
        </section>

        {/* SIDE / BOTTOM PANEL */}
        <section
          className={`${orientation === "landscape" ? "flex-[1]" : "flex-[1]"} flex flex-col min-h-0 bg-[#F7F4EB] text-ink`}
          data-testid="chairside-panel"
        >
          {/* Panel header */}
          {selectedTooth ? (
            <div className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-[#E8EAE9] bg-white">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-pine text-linen grid place-items-center font-display text-2xl shrink-0" data-testid="chairside-tooth-number">
                  {selectedTooth}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft">FDI · {info.isUpper ? "Upper" : "Lower"} · {info.isPrimary ? "Primary" : "Permanent"}</div>
                  <div className="font-display text-lg leading-tight truncate">{info.name}</div>
                  <StatusPill conditions={currentTooth?.conditions || []} />
                </div>
              </div>

              {/* Surfaces (tap to paint) */}
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1.5">Surfaces</div>
                <div className="flex flex-wrap gap-1.5">
                  {info.surfaces.map((s) => {
                    const on = selectedSurfaces.includes(s);
                    return (
                      <button
                        key={s}
                        data-testid={`chairside-surface-${s}`}
                        onClick={() => setSelectedSurfaces((p) => on ? p.filter((x) => x !== s) : [...p, s])}
                        className={`min-w-[44px] min-h-[36px] px-3 rounded-full border text-sm font-medium transition ${on
                          ? "bg-pine text-linen border-pine"
                          : "border-pine/20 text-ink bg-white hover:bg-linen"}`}
                        title={SURFACE_LABELS[s]}>{s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="shrink-0 px-6 py-4 border-b border-[#E8EAE9] bg-white text-center text-ink-soft text-sm">
              No tooth selected.
            </div>
          )}

          {/* Tab bar */}
          <div className="shrink-0 flex border-b border-[#E8EAE9] bg-white" role="tablist" data-testid="chairside-tabs">
            {TABS.map((t) => {
              const on = tab === t.key;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={`chairside-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  disabled={!selectedTooth}
                  role="tab" aria-selected={on}
                  className={`flex-1 min-h-[52px] flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium border-b-2 transition
                    ${on ? "border-pine text-ink" : "border-transparent text-ink-soft"}
                    disabled:opacity-30 disabled:cursor-not-allowed`}>
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 pb-24">
            {!selectedTooth ? (
              <div className="text-center py-12 text-ink-soft text-sm">
                Pick a tooth from the chart to begin.
              </div>
            ) : tab === "conditions" ? (
              <ConditionsTab
                currentTooth={currentTooth}
                selectedSurfaces={selectedSurfaces}
                onApply={applyCondition}
                onRemove={removeCondition}
                onOpenPalette={() => setPaletteOpen(true)}
              />
            ) : tab === "notes" ? (
              <NotesTab
                notesDraft={notesDraft}
                onChange={setNotesDraft}
                onSave={saveNotes}
                saving={savingNotes}
                unchanged={notesDraft === (currentTooth?.notes || "")}
              />
            ) : tab === "voice" ? (
              <VoiceTab
                onAppend={(txt) => setNotesDraft((n) => (n ? n + " " : "") + txt)}
                onSwitchToNotes={() => setTab("notes")}
                currentNotes={notesDraft}
              />
            ) : (
              <PhotosTab
                toothFiles={toothFiles}
                uploading={uploading}
                onUpload={() => fileInputRef.current?.click()}
                onCamera={() => cameraInputRef.current?.click()}
              />
            )}

            {/* Hidden inputs (used by Photos tab and camera capture button) */}
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf"
                   className="hidden" onChange={uploadPhoto} data-testid="chairside-file-input" />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                   className="hidden" onChange={uploadPhoto} data-testid="chairside-camera-input" />
          </div>
        </section>
      </div>

      {/* Sticky bottom action bar */}
      <div
        data-testid="chairside-action-bar"
        className="shrink-0 h-16 bg-[#0B1712] border-t border-white/5 px-2 sm:px-4 flex items-center justify-between gap-2"
      >
        <button
          data-testid="chairside-prev-tooth"
          onClick={() => gotoAdjacent("prev")}
          className="h-11 px-3 sm:px-4 rounded-full text-sm flex items-center gap-1.5 bg-white/5 hover:bg-white/10 active:scale-95 transition">
          <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Prev</span>
        </button>

        <div className="flex-1 text-center min-w-0">
          {selectedTooth ? (
            <div className="text-xs sm:text-sm text-white/70 truncate">
              <span className="font-mono text-linen">{selectedTooth}</span>
              <span className="mx-1.5 text-white/30">·</span>
              <span className="truncate">{info?.name}</span>
              {selectedSurfaces.length > 0 && (
                <span className="ml-2 text-[10px] uppercase tracking-widest text-clay">
                  Painting {selectedSurfaces.join(",")}
                </span>
              )}
            </div>
          ) : (
            <div className="text-xs text-white/40">Tap a tooth or swipe to begin</div>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {selectedTooth && tab === "notes" && (
            <button
              data-testid="chairside-save-notes"
              onClick={saveNotes}
              disabled={savingNotes || notesDraft === (currentTooth?.notes || "")}
              className="h-11 px-4 rounded-full text-sm inline-flex items-center gap-1.5 bg-linen text-ink hover:bg-white active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed">
              {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          )}
          <button
            data-testid="chairside-next-tooth"
            onClick={() => gotoAdjacent("next")}
            className="h-11 px-3 sm:px-4 rounded-full text-sm flex items-center gap-1.5 bg-white/5 hover:bg-white/10 active:scale-95 transition">
            <span className="hidden sm:inline">Next</span> <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Universal treatment palette (Ctrl/⌘+K) */}
      <TreatmentPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        patientId={id}
        tooth={selectedTooth}
        selectedSurfaces={selectedSurfaces}
        onApply={planTreatment}
      />
    </div>
  );
}

/* ============================================================================
   Chart arch — reuses ToothSVG but bumps its parent's font-size so touch
   targets are ~40% larger than the dashboard grid.
   ========================================================================== */
function ChairArch({ teeth, chart, selectedTooth, onTooth, selectedSurfaces, onSurface, title }) {
  const gridCols = teeth["upper-right"].length + teeth["upper-left"].length;
  return (
    <div className="inline-block w-full">
      {title && <div className="text-[10px] uppercase tracking-widest text-white/40 text-center mb-2">{title}</div>}
      <div className="chairside-arch space-y-3">
        <div className="grid gap-1 justify-items-center" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0,1fr))` }}>
          {[...teeth["upper-right"], ...teeth["upper-left"]].map((t) => (
            <ToothSVG key={t} tooth={t}
              conditions={chart.teeth[t]?.conditions || []}
              isSelected={selectedTooth === t}
              selectedSurfaces={selectedTooth === t ? selectedSurfaces : []}
              onTooth={onTooth} onSurface={onSurface} />
          ))}
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="grid gap-1 justify-items-center" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0,1fr))` }}>
          {[...teeth["lower-right"], ...teeth["lower-left"]].map((t) => (
            <ToothSVG key={t} tooth={t}
              conditions={chart.teeth[t]?.conditions || []}
              isSelected={selectedTooth === t}
              selectedSurfaces={selectedTooth === t ? selectedSurfaces : []}
              onTooth={onTooth} onSurface={onSurface} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
   Tabs
   ========================================================================== */

function ConditionsTab({ currentTooth, selectedSurfaces, onApply, onRemove, onOpenPalette }) {
  return (
    <div className="space-y-6">
      {/* Existing conditions */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">On this tooth</div>
        {(currentTooth?.conditions || []).length === 0 ? (
          <div className="text-sm text-ink-soft italic">No conditions recorded.</div>
        ) : (
          <ul className="space-y-1.5">
            {currentTooth.conditions.map((c) => {
              const meta = CONDITION_META[c.code] || { label: c.code };
              return (
                <li key={c.id}
                    className="flex items-center gap-2 bg-white rounded-xl border border-[#E8EAE9] px-3 py-2"
                    data-testid={`chairside-cond-${c.id}`}>
                  <ConditionIcon code={c.code} size={22} />
                  <span className="text-sm text-ink flex-1 truncate">{meta.label}</span>
                  {c.surfaces?.length > 0 && (
                    <span className="text-[10px] text-ink-soft font-mono">{c.surfaces.join(",")}</span>
                  )}
                  <button
                    data-testid={`chairside-remove-${c.id}`}
                    onClick={() => onRemove(c.id)}
                    className="w-9 h-9 rounded-full grid place-items-center text-ink-soft hover:text-clay hover:bg-linen">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Quick apply */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Add condition</div>
        {selectedSurfaces.length > 0 && (
          <div className="text-[11px] text-clay mb-2">
            Applying to surfaces: <b>{selectedSurfaces.join(", ")}</b>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {QUICK_CONDITIONS.map((code) => {
            const m = CONDITION_META[code];
            return (
              <button
                key={code}
                data-testid={`chairside-apply-${code}`}
                onClick={() => onApply(code)}
                className="min-h-[64px] flex flex-col items-center justify-center gap-1 rounded-2xl border border-[#E8EAE9] bg-white hover:border-pine/40 hover:bg-linen active:scale-95 transition p-2">
                <ConditionIcon code={code} size={26} />
                <span className="text-[11px] text-ink text-center leading-tight">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Plan a treatment */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Plan a treatment</div>
        <button
          type="button"
          onClick={onOpenPalette}
          data-testid="chairside-open-treatment-palette"
          className="w-full min-h-[64px] rounded-2xl bg-gradient-to-br from-pine to-[#0F251C] text-linen hover:from-pine hover:to-pine active:scale-[0.98] transition p-4 flex items-center justify-between gap-3 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-linen/10 grid place-items-center">
              <Plus className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium">Add treatment</div>
              <div className="text-[11px] text-linen/60 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Suggested + full Treatment Master · search anything
              </div>
            </div>
          </div>
          <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-linen/20 text-linen/70">
            ⌘ K
          </kbd>
        </button>
      </div>
    </div>
  );
}

function NotesTab({ notesDraft, onChange, onSave, saving, unchanged }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Clinical notes for this tooth</div>
      <textarea
        data-testid="chairside-notes-textarea"
        value={notesDraft}
        onChange={(e) => onChange(e.target.value)}
        rows={10}
        placeholder="Free-form clinical notes for this tooth. Use the Voice tab to dictate hands-free."
        className="w-full rounded-2xl border border-[#E8EAE9] bg-white p-4 text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-pine/40 resize-none min-h-[220px]"
      />
      <button
        data-testid="chairside-notes-save"
        onClick={onSave}
        disabled={saving || unchanged}
        className="mt-3 h-12 px-5 rounded-full text-sm inline-flex items-center gap-2 bg-pine text-linen hover:bg-pine/90 disabled:opacity-40">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save note
      </button>
    </div>
  );
}

/* ---------- Voice dictation via browser SpeechRecognition ---------- */
function VoiceTab({ onAppend, onSwitchToNotes, currentNotes }) {
  const [supported, setSupported] = useState(true);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-IN";
    rec.onresult = (ev) => {
      let final = "";
      let inter = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) final += r[0].transcript + " ";
        else           inter += r[0].transcript;
      }
      if (final.trim()) {
        onAppend(final.trim());
        setInterim("");
      } else {
        setInterim(inter);
      }
    };
    rec.onerror = () => { setListening(false); };
    rec.onend = () => { setListening(false); setInterim(""); };
    recognitionRef.current = rec;
    return () => {
      try { rec.stop(); } catch {}
      recognitionRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) { try { rec.stop(); } catch {} }
    else { try { rec.start(); setListening(true); } catch {} }
  };

  if (!supported) {
    return (
      <div className="rounded-2xl bg-white border border-[#E8EAE9] p-6 text-center">
        <MicOff className="w-8 h-8 text-ink-soft mx-auto mb-3" />
        <div className="text-sm text-ink-mid mb-1">Voice dictation not supported on this browser.</div>
        <div className="text-xs text-ink-soft">Please use Safari on iPad, or Chrome. You can still type notes in the Notes tab.</div>
        <button onClick={onSwitchToNotes} className="mt-4 text-xs underline text-clay">Open Notes</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white border border-[#E8EAE9] p-6 text-center">
        <button
          data-testid="chairside-voice-toggle"
          onClick={toggle}
          className={`w-24 h-24 rounded-full mx-auto grid place-items-center transition active:scale-95 ${
            listening
              ? "bg-clay text-linen animate-pulse shadow-[0_0_0_8px_rgba(208,106,66,0.15)]"
              : "bg-pine text-linen hover:bg-pine/90"
          }`}
          aria-label={listening ? "Stop listening" : "Start dictation"}>
          {listening ? <MicOff className="w-10 h-10" /> : <Mic className="w-10 h-10" />}
        </button>
        <div className="mt-3 text-sm font-medium text-ink">
          {listening ? "Listening… tap to stop" : "Tap to dictate"}
        </div>
        <div className="mt-1 text-[11px] text-ink-soft">
          Speech is transcribed on-device and appended to this tooth&apos;s notes.
        </div>
      </div>

      {interim && (
        <div className="rounded-xl bg-linen border border-dashed border-pine/30 p-3 text-sm text-ink-mid italic">
          {interim}…
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E8EAE9] p-4">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1.5">Notes preview</div>
        <div className="text-sm text-ink whitespace-pre-wrap min-h-[60px]" data-testid="chairside-voice-preview">
          {currentNotes || <span className="text-ink-soft italic">No notes yet — start dictating.</span>}
        </div>
        <button onClick={onSwitchToNotes} className="mt-3 text-xs text-clay hover:underline">
          Edit in Notes →
        </button>
      </div>
    </div>
  );
}

/* ---------- Photos: quick camera + upload, auto-linked to tooth ---------- */
function PhotosTab({ toothFiles, uploading, onUpload, onCamera }) {
  const [thumbs, setThumbs] = useState({}); // fid -> blob url

  useEffect(() => {
    const urls = [];
    (async () => {
      for (const f of toothFiles) {
        if (thumbs[f.id]) continue;
        if (!(f.content_type || "").startsWith("image/")) continue;
        try {
          const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          urls.push(url);
          setThumbs((prev) => ({ ...prev, [f.id]: url }));
        } catch { /* silent */ }
      }
    })();
    return () => { urls.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toothFiles]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <button
          data-testid="chairside-camera-btn"
          onClick={onCamera}
          disabled={uploading}
          className="min-h-[96px] rounded-2xl bg-pine text-linen flex flex-col items-center justify-center gap-2 hover:bg-pine/90 active:scale-95 transition disabled:opacity-60">
          <Camera className="w-7 h-7" />
          <span className="text-sm font-medium">Take photo</span>
        </button>
        <button
          data-testid="chairside-upload-btn"
          onClick={onUpload}
          disabled={uploading}
          className="min-h-[96px] rounded-2xl border-2 border-dashed border-pine/30 bg-white text-ink flex flex-col items-center justify-center gap-2 hover:border-pine hover:bg-linen active:scale-95 transition disabled:opacity-60">
          {uploading ? <Loader2 className="w-7 h-7 animate-spin" /> : <ImageIcon className="w-7 h-7" />}
          <span className="text-sm font-medium">{uploading ? "Uploading…" : "Upload file"}</span>
        </button>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">
          Attached to this tooth · {toothFiles.length}
        </div>
        {toothFiles.length === 0 ? (
          <div className="rounded-2xl bg-white border border-[#E8EAE9] p-6 text-center text-ink-soft text-sm">
            No X-rays or photos linked to this tooth yet. Use the camera to capture chairside.
          </div>
        ) : (
          <ul className="grid grid-cols-2 gap-2">
            {toothFiles.map((f) => (
              <li key={f.id}
                  className="rounded-xl border border-[#E8EAE9] bg-white overflow-hidden"
                  data-testid={`chairside-tooth-file-${f.id}`}>
                <div className="aspect-square bg-linen grid place-items-center">
                  {(f.content_type || "").startsWith("image/") && thumbs[f.id] ? (
                    <img src={thumbs[f.id]}
                         alt={f.original_filename}
                         className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-ink-disabled" />
                  )}
                </div>
                <div className="p-2 text-[11px] truncate text-ink" title={f.original_filename}>
                  {f.original_filename}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */
function StatusPill({ conditions }) {
  const priority = ["missing", "extracted", "extraction_indicated", "rct", "crown", "implant", "caries"];
  let code = null;
  for (const p of priority) if (conditions.some((c) => c.code === p)) { code = p; break; }
  if (!code)
    return (
      <div className="mt-1 inline-block text-[10px] uppercase tracking-widest text-[#2D6A4F] bg-[#E8F0EB] rounded-full px-2 py-0.5">
        Healthy
      </div>
    );
  const m = CONDITION_META[code];
  return (
    <div className="mt-1 inline-block text-[10px] uppercase tracking-widest text-linen rounded-full px-2 py-0.5"
         style={{ background: m.color }}>
      {m.label}
    </div>
  );
}
